/**
 * claim.ts — the single place the raw session claim is validated, decoded
 * and the principal role is branded.
 *
 * Nothing declared in this file is re-exported from `./index`
 * (ADR-20260826-79rv): `CapabilityClaim`, `PrincipalRole` and the raw
 * `RawSessionClaim` wire shape are never given an exported name anywhere
 * in the repository OUTSIDE `claim.ts`, `provider.ts` and
 * `CapabilityProvider.tsx` — `provider.ts` exports `RawSessionClaim`
 * itself (and `capability/stub/stub-provider.ts` re-exports it from
 * there), so the unqualified claim above is false; see
 * `../README.md`'s "What never leaves it" section for the qualified,
 * correct statement this header must match. `capability.ts` and
 * `renewal-id.ts` (both inside
 * this directory) consume `decodeClaim`'s shape only through inference —
 * `Parameters<typeof decodeClaim>[0]` and the decoded value's own
 * inferred return type — never by importing a type name. Structural
 * containment is the primary control here; the dependency-cruiser rule
 * `m1-portal-capability-boundary` appends to `.dependency-cruiser.js`
 * (TG-6) is a second, independent layer over the same property, not the
 * only one (see `__tests__/containment.test.ts`).
 *
 * `decodeClaim` itself IS exported (so the other files in this directory
 * can call it) but is not re-exported from `./index` either — a module
 * outside `capability/` can reach it only with a deep relative import,
 * which is exactly what `__tests__/containment.test.ts`'s standalone
 * dependency-cruiser check (ADR-20260826-21nr) proves is forbidden.
 *
 * Claim field names and the closed field set match the published
 * contract this module compiles against: `src/api/contracts/session-
 * claim.schema.json` version 1.0.0 (ADR-20260825-bbm3, ADR-20260826-zq85;
 * the version pin itself is `./claim-version.ts`'s concern, not this
 * file's).
 */

/**
 * The untrusted, wire-shaped claim as it arrives from the session layer.
 * Every field is optional here because the whole point of this type is
 * to describe data this module has not yet validated — an absent,
 * truncated or malformed claim is a value of this same type, not a
 * different one. `principalRole` is a plain `string`, not the closed
 * `"owner" | "contributor"` union, because a real claim can carry any
 * string (or none) and this module must be able to recognise that case
 * rather than reject it at the type layer before it has a chance to
 * decide and record it as a validation failure.
 */
interface RawSessionClaim {
  readonly principalRole?: string;
  readonly renewalId?: string;
  readonly delegatedSectionIds?: readonly string[];
}

/**
 * The closed field set declared by `src/api/contracts/session-
 * claim.schema.json`'s `additionalProperties: false`. Hand-enforced here
 * rather than through a schema-validation library (`ajv` is a
 * devDependency only — adding it to the production bundle is a new
 * runtime dependency this module does not take) because the shape is
 * small, closed and already fully re-declared in {@link RawSessionClaim}.
 * A claim carrying any key outside this set is schema-invalid and must
 * be denied, not silently accepted on the three fields this module
 * happens to look at.
 */
const RAW_SESSION_CLAIM_FIELDS = new Set(["principalRole", "renewalId", "delegatedSectionIds"]);

/**
 * FIND-717/FIND-718 (DEBT-20261013-1g1z): `Reflect.ownKeys` rather than
 * `Object.keys`, so a non-enumerable or symbol-keyed sibling cannot hide from
 * the closed-set check, and every key must itself be an enumerable own
 * string field of the closed set -- a field `JSON.parse` could never have
 * produced as non-enumerable is a field a wire claim never carries.
 */
function hasOnlyClosedFieldSet(claim: object): boolean {
  return Reflect.ownKeys(claim).every(
    (key) =>
      typeof key === "string" &&
      RAW_SESSION_CLAIM_FIELDS.has(key) &&
      Reflect.getOwnPropertyDescriptor(claim, key)?.enumerable === true,
  );
}

/**
 * FIND-717 (DEBT-20261013-1g1z): a Proxy whose `ownKeys`,
 * `getOwnPropertyDescriptor` and `get` traps report a closed field set and
 * fabricated values passes every structural check on the claim itself,
 * because each check asks the Proxy and believes the answer. `structuredClone`
 * throws on a Proxy (and on any other exotic, non-cloneable object), so the
 * claim is snapshotted into a plain data object first and every later read in
 * {@link decodeClaim} is of that snapshot, never of the caller's object. A
 * claim parsed from server JSON is always cloneable, so this rejects nothing
 * legitimate. Returns `null` when the claim cannot be cloned.
 */
function snapshotClaim(claim: object): RawSessionClaim | null {
  try {
    return structuredClone(claim) as RawSessionClaim;
  } catch {
    return null;
  }
}

/**
 * OBJ-S-43 (FIND-705, debate turn 5): {@link hasOnlyClosedFieldSet} enumerates
 * OWN keys only (`Object.keys`), but every field read below it
 * (`claim.principalRole`, `claim.renewalId`, `claim.delegatedSectionIds`) is a
 * plain property access, which walks the prototype chain. A claim built with
 * `Object.create(maliciousPrototype)` carrying no own keys at all passes the
 * closed-field-set check vacuously (`Object.keys` returns `[]`) while still
 * reading an inherited `principalRole` of `"owner"` from its prototype,
 * decoding as a valid owner claim. Reject any claim whose prototype is not
 * the ordinary object prototype or `null` (a `JSON.parse`'d wire claim never
 * has any other prototype) before the own-key check runs, so an inherited
 * field can never be read as though it were the claim's own.
 */
function hasOrdinaryPrototype(claim: object): boolean {
  const prototype = Object.getPrototypeOf(claim) as object | null;
  return prototype === null || prototype === Object.prototype;
}

function hasUniqueDelegatedSectionIds(value: readonly string[]): boolean {
  return new Set(value).size === value.length;
}

declare const principalRoleBrand: unique symbol;

/**
 * ADR-20260826-wavw: a union of `owner` and `contributor`, intersected
 * with a phantom property keyed by a unique symbol that exists only at
 * the type level (it is erased at runtime, so an `===` comparison
 * against the literal `"owner"` still works). The brand is applied in
 * exactly one place in this file — `decodeClaim`'s `as PrincipalRole` —
 * the single sanctioned cast in this module. Nothing outside this file
 * can produce a value of this type, because nothing outside this file
 * can write that cast.
 */
type PrincipalRole = ("owner" | "contributor") & { readonly [principalRoleBrand]: true };

/**
 * The validated, decoded claim. Declared here, used only by this file's
 * own `decodeClaim` return type and consumed elsewhere in `capability/`
 * through inference — never imported by name (see file header).
 */
interface CapabilityClaim {
  readonly principalRole: PrincipalRole;
  readonly renewalId: string;
  readonly delegatedSectionIds: readonly string[];
}

function isValidPrincipalRole(value: string | undefined): value is "owner" | "contributor" {
  return value === "owner" || value === "contributor";
}

function isValidDelegatedSectionIds(value: readonly string[] | undefined): value is readonly string[] {
  return Array.isArray(value) && value.every((sectionId) => typeof sectionId === "string" && sectionId.length > 0);
}

/**
 * Validates and decodes a raw session claim into {@link CapabilityClaim}.
 * Returns `null` on an absent, schema-invalid, role-missing or
 * unrecognised-role claim — deny by default (ADR-20260826-82qe) — and
 * never throws, so a caller cannot accidentally treat a thrown exception
 * as "permitted". An unrecognised role is a validation failure, never
 * defaulted to `contributor` and never to `owner`.
 */
export function decodeClaim(claim: RawSessionClaim | null | undefined): CapabilityClaim | null {
  if (claim === null || claim === undefined) {
    return null;
  }
  if (typeof claim !== "object") {
    return null;
  }
  // Snapshot first: a Proxy is rejected before any trap can answer a check.
  const snapshot = snapshotClaim(claim);
  if (snapshot === null) {
    return null;
  }
  if (!hasOrdinaryPrototype(claim) || !hasOnlyClosedFieldSet(claim)) {
    return null;
  }
  if (!isValidPrincipalRole(snapshot.principalRole)) {
    return null;
  }
  if (typeof snapshot.renewalId !== "string" || snapshot.renewalId.length === 0) {
    return null;
  }
  if (!isValidDelegatedSectionIds(snapshot.delegatedSectionIds)) {
    return null;
  }
  if (!hasUniqueDelegatedSectionIds(snapshot.delegatedSectionIds)) {
    return null;
  }
  return {
    principalRole: snapshot.principalRole as PrincipalRole,
    renewalId: snapshot.renewalId,
    delegatedSectionIds: snapshot.delegatedSectionIds,
  };
}
